import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { PageSidesheetActions } from '@next/core-lib/components';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { TableColumnTypes, TableConfig } from '@next/core-lib/table';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, ReplaySubject } from 'rxjs';
import { map, startWith, take, takeUntil } from 'rxjs/operators';
import { PhosphateLegalWorkingCoefficient, PhosphateLegalWorkingCoefficientsFilter, SiteState } from 'src/app/shared/models';
import {
  getCropAids,
  getFertilizationSalesOrderPeriods,
  getPhosphateLegalWorkingCoefficients,
  getPhosphateLegalWorkingCoefficientsFilter,
  getPhosphateLegalWorkingCoefficientsNumberOfItems,
  getSoilTypes,
} from 'src/app/store/selectors';

import {
  AddPhosphateLegalWorkingCoefficient,
  ClearPhosphateLegalWorkingCoefficientsFilter,
  DeletePhosphateLegalWorkingCoefficient,
  EditPhosphateLegalWorkingCoefficient,
  LoadPhosphateLegalWorkingCoefficientsRequest,
  UpdatePhosphateLegalWorkingCoefficientsFilter,
} from '../../../store/actions';

@Component({
  selector: 'app-phosphate-legal-working-coefficient-list',
  templateUrl: './phosphate-legal-working-coefficient-list.component.html',
  standalone: false,
})
export class PhosphateLegalWorkingCoefficientListComponent implements OnInit, OnDestroy {
  private readonly breakpoint = inject<BreakpointService>(BreakpointServiceInjector);
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly dialog = inject(DialogService);
  private readonly locale = inject(LocaleService);

  request: {
    pageNumber: number;
    pageSize: number;
    filter: PhosphateLegalWorkingCoefficientsFilter;
  } = {
    pageNumber: 1,
    pageSize: 10,
    filter: {},
  };
  isMobile$ = this.breakpoint.ltLg$;
  coefficients$ = this.store.pipe(select(getPhosphateLegalWorkingCoefficients));
  soilTypes$ = this.store.pipe(select(getSoilTypes));
  cropAids$ = this.store.pipe(select(getCropAids));
  salesOrderPeriods$ = this.store.pipe(select(getFertilizationSalesOrderPeriods));
  tableConfig: TableConfig = {
    dataSource: new MatTableDataSource(),
    selectColumn: false,
    pagination: false,
    header: true,
    footer: false,
    disableCards: true,
    columns: [
      {
        id: 'calendarYear',
        label: this.locale.translate('year'),
        type: TableColumnTypes.STRING,
      },
      {
        id: 'cropAidName',
        label: this.locale.translate('cropAid'),
        type: TableColumnTypes.STRING,
      },
      {
        id: 'phosphateWorkingCoefficient',
        label: this.locale.translate('phosphateLegalWorkingCoefficient'),
        type: TableColumnTypes.NUMBER,
      },
    ],
    actions: [
      {
        id: 'edit',
        label: this.locale.translate('edit'),
        icon: 'edit',
        callback: (event: PhosphateLegalWorkingCoefficient) => this.editItem(event),
        disabled: () => false,
      },
      {
        id: 'delete',
        label: this.locale.translate('delete'),
        icon: 'delete',
        callback: (event: PhosphateLegalWorkingCoefficient) => this.deleteItem(event),
        disabled: () => false,
      },
    ],
  };
  totalNumberOfItems$ = this.store.pipe(select(getPhosphateLegalWorkingCoefficientsNumberOfItems));
  filterControl = new FormControl();
  filterValue$ = this.filterControl.valueChanges.pipe(
    map((value: string) => value || ''),
    startWith(''),
  );
  filterEnabled$ = new BehaviorSubject(false);
  isEmpty$ = combineLatest([this.filterEnabled$, this.totalNumberOfItems$]).pipe(
    map(([filterEnabled, totalNumberOfItems]) => !filterEnabled && totalNumberOfItems === 0),
  );
  phosphateLegalWorkingCoefficientsFilter$ = this.store.pipe(select(getPhosphateLegalWorkingCoefficientsFilter));
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  ngOnInit() {
    combineLatest([this.coefficients$, this.soilTypes$, this.cropAids$, this.salesOrderPeriods$])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([coefficients, soilTypes, cropAids, salesOrderPeriods]) => {
        if (!(coefficients.length && soilTypes?.length && cropAids?.length && salesOrderPeriods?.length)) {
          return;
        }
        this.tableConfig.dataSource.data = coefficients.map((coefficient) => {
          const cropAidName = cropAids.find((c) => c.id === coefficient.cropAidId)?.name;
          return {
            ...coefficient,
            cropAidName,
          };
        });
      });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  updateData() {
    this.store.dispatch(new LoadPhosphateLegalWorkingCoefficientsRequest(this.request.pageNumber, this.request.pageSize, this.request.filter));
  }

  updateFilter(data: PhosphateLegalWorkingCoefficientsFilter) {
    this.request.filter = data;
    this.request.pageNumber = 1;
    this.store.dispatch(new UpdatePhosphateLegalWorkingCoefficientsFilter(data));
    this.store.dispatch(new LoadPhosphateLegalWorkingCoefficientsRequest(this.request.pageNumber, this.request.pageSize, this.request.filter));
  }

  setFilterEnabled(isEnabled: boolean) {
    this.filterEnabled$.next(isEnabled);
  }

  resetFilter() {
    this.store.dispatch(new ClearPhosphateLegalWorkingCoefficientsFilter());
  }

  setSearch() {
    this.store.dispatch(PageSidesheetActions.openSidesheet());
  }

  addItem() {
    this.store.dispatch(new AddPhosphateLegalWorkingCoefficient());
  }

  editItem(item: PhosphateLegalWorkingCoefficient) {
    this.store.dispatch(new EditPhosphateLegalWorkingCoefficient(item));
  }

  deleteItem(item: PhosphateLegalWorkingCoefficient) {
    this.dialog
      .confirm({
        title: this.locale.translate('delete'),
        text: this.locale.translate('deleteItemWarning'),
        pushHistoryState: false,
      })
      .pipe(take(1))
      .subscribe((x) => {
        if (x.status === DIALOG_RETURN_TYPES.confirm) {
          this.store.dispatch(new DeletePhosphateLegalWorkingCoefficient(item));
          this.updateData();
        }
      });
  }
}
